import { InvitationDesign } from '../../_types/invitation-design-v2';
import ORDER from './_order.json';

// TEMPLATES ARE DATA. Every design in this folder is a plain `.json` file, and
// this module discovers them — there is no import list and no registration.
// Adding a template is adding ONE FILE; deleting one is deleting it.
//
// Why that matters beyond tidiness: the design format is already a JSON
// document that the API stores in `invitation_types.design_customizations` and
// accepts wholesale through `saveInvitationTypeDesign`. Keeping the starter
// library as TypeScript was the one place the format pretended to be code, and
// it meant an agent authoring a template had to edit four separate places in
// this file to make it appear.
//
// They stay as FILES rather than moving into the database on purpose: a
// template is then diffable, reviewable and revertible like anything else in
// the repository. When customers need to own private templates that has to
// become a table, and these files become its seed data — see
// docs/design/invitation-templates.md.
const modules = import.meta.glob<{ default: InvitationDesign }>('./*.json', { eager: true });

const byId = new Map<string, InvitationDesign>();
for (const [path, mod] of Object.entries(modules)) {
  // _order.json is a manifest, not a design.
  if (path.endsWith('/_order.json')) continue;
  const design = mod.default;
  if (design?.template?.id) byId.set(design.template.id, design);
}

// DISPLAY ORDER, and it is load-bearing: the designer opens on
// INVITATION_TEMPLATES_V2[0], so a bare alphabetical sort would silently
// change which template every new user sees first.
//
// _order.json is therefore a preference, NOT a registration — anything missing
// from it still appears, sorted by name at the end. So a new file needs no
// edit here; listing it only decides where it sits.
const ranked = ORDER.map((id) => byId.get(id)).filter((d): d is InvitationDesign => !!d);
const unranked = [...byId.values()]
  .filter((d) => !ORDER.includes(d.template.id))
  .sort((a, b) => a.template.name.localeCompare(b.template.name));

export const INVITATION_TEMPLATES_V2: InvitationDesign[] = [...ranked, ...unranked];

/** Templates grouped by their own `category`, derived rather than hand-listed. */
export const templatesByCategory: Record<string, InvitationDesign[]> =
  INVITATION_TEMPLATES_V2.reduce<Record<string, InvitationDesign[]>>((acc, design) => {
    (acc[design.template.category] ??= []).push(design);
    return acc;
  }, {});

const inCategories = (...names: string[]) =>
  INVITATION_TEMPLATES_V2.filter((d) => names.includes(d.template.category));

// These five were hand-maintained arrays that nothing in the app imported.
// Kept as derived exports so the surface does not change, but note the
// difference: `weddingTemplates` used to be a list somebody curated, and is now
// exactly the templates whose own category says so. The originals disagreed —
// classic-elegant is category 'classic', modern-minimal is 'modern' — so those
// three are picked up explicitly here rather than silently dropped.
export const weddingTemplates = inCategories('wedding', 'classic', 'modern', 'rustic');
export const engagementTemplates = inCategories('engagement');
export const saveDateTemplates = inCategories('save-the-date');
export const birthdayTemplates = inCategories('birthday');
export const anniversaryTemplates = inCategories('anniversary');
export const partyTemplates = inCategories('party');
